import React, { useState, useMemo } from 'react';
import {
  X,
  Package,
  FileText,
  Sparkles,
  Check,
  ArrowRight,
  Plus,
  Layers,
  Search,
  ExternalLink,
} from 'lucide-react';
import {
  SITE_BUNDLES,
  ALL_INTERCHANGEABLE_PAGE_TEMPLATES,
  type WebsiteBundle,
  type BundlePageTemplate,
  type PageArchetype,
  applyPageTemplateToSiteChrome,
} from '../templates/siteBundles';
import type { BlockNode } from '../types';

export interface TemplateBundlePickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentRoot: BlockNode;
  onApplyTemplateToCurrentPage: (newRoot: BlockNode) => void;
  onApplyFullBundle: (bundle: WebsiteBundle) => void;
  onAddNewPageWithTemplate: (template: BundlePageTemplate) => void;
}

export const TemplateBundlePickerModal: React.FC<TemplateBundlePickerModalProps> = ({
  isOpen,
  onClose,
  currentRoot,
  onApplyTemplateToCurrentPage,
  onApplyFullBundle,
  onAddNewPageWithTemplate,
}) => {
  const [activeTab, setActiveTab] = useState<'bundles' | 'templates'>('bundles');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedArchetype, setSelectedArchetype] = useState<string>('all');
  const [selectedBundlePreview, setSelectedBundlePreview] = useState<WebsiteBundle | null>(null);

  const filteredBundles = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return SITE_BUNDLES;
    return SITE_BUNDLES.filter(
      (b) =>
        b.fancyName.toLowerCase().includes(q) ||
        b.tagline.toLowerCase().includes(q) ||
        b.targetAudience.toLowerCase().includes(q)
    );
  }, [searchQuery]);

  const filteredTemplates = useMemo(() => {
    let list = ALL_INTERCHANGEABLE_PAGE_TEMPLATES;
    if (selectedArchetype !== 'all') {
      list = list.filter((t) => t.archetype === selectedArchetype);
    }
    const q = searchQuery.toLowerCase().trim();
    if (q) {
      list = list.filter(
        (t) =>
          t.title.toLowerCase().includes(q) ||
          t.bundleName.toLowerCase().includes(q) ||
          t.description.toLowerCase().includes(q)
      );
    }
    return list;
  }, [selectedArchetype, searchQuery]);

  if (!isOpen) return null;

  const handleApplyToCurrent = (tpl: BundlePageTemplate) => {
    const templateRoot = tpl.generateRoot('new-root', tpl.bundleName);
    const mergedRoot = applyPageTemplateToSiteChrome(templateRoot, currentRoot);
    onApplyTemplateToCurrentPage(mergedRoot);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-3 sm:p-6 animate-in fade-in duration-150">
      <div className="bg-card text-foreground border border-border w-full max-w-5xl rounded-2xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-5 py-3.5 border-b border-border flex items-center justify-between bg-muted/30 shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
              <Sparkles className="h-4 w-4" />
            </div>
            <div>
              <h2 className="text-sm sm:text-base font-bold leading-tight">
                Website Bundles & Interchangeable Page Templates
              </h2>
              <p className="text-[11px] text-muted-foreground leading-none mt-0.5">
                Pick a complete 5-page coaching website bundle or mix & match individual pre-built pages
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Tab & Search Bar */}
        <div className="px-5 py-2.5 border-b border-border bg-muted/10 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 shrink-0">
          <div className="flex items-center gap-1 bg-muted/60 p-0.5 rounded-lg border border-border">
            <button
              type="button"
              onClick={() => setActiveTab('bundles')}
              className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-bold transition-all cursor-pointer ${
                activeTab === 'bundles'
                  ? 'bg-background text-primary shadow-xs'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Package className="h-3.5 w-3.5" />
              <span>Full Website Bundles (10)</span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('templates')}
              className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-bold transition-all cursor-pointer ${
                activeTab === 'templates'
                  ? 'bg-background text-primary shadow-xs'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <FileText className="h-3.5 w-3.5" />
              <span>Interchangeable Pages (50+)</span>
            </button>
          </div>

          <div className="relative flex items-center max-w-xs w-full">
            <Search className="absolute left-2.5 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              placeholder={activeTab === 'bundles' ? 'Search 10 bundles...' : 'Search page templates...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-2.5 py-1 text-xs rounded-lg border border-input bg-background placeholder:text-muted-foreground focus:outline-none focus:border-primary"
            />
          </div>
        </div>

        {/* Archetype Filter Pills (Only for Templates Tab) */}
        {activeTab === 'templates' && (
          <div className="px-5 py-2 border-b border-border/60 bg-muted/5 flex items-center gap-1.5 overflow-x-auto no-scrollbar shrink-0 text-xs">
            {[
              { id: 'all', label: 'All Pages' },
              { id: 'landing', label: 'Landing Pages' },
              { id: 'courses', label: 'Courses Pages' },
              { id: 'profile', label: 'Faculty & Mentors' },
              { id: 'results', label: 'Results & Ranks' },
              { id: 'admissions', label: 'Admissions & Desk' },
            ].map((arch) => (
              <button
                key={arch.id}
                type="button"
                onClick={() => setSelectedArchetype(arch.id)}
                className={`px-2.5 py-0.5 rounded-full text-[11px] font-medium transition-colors cursor-pointer shrink-0 ${
                  selectedArchetype === arch.id
                    ? 'bg-primary text-primary-foreground font-semibold shadow-xs'
                    : 'bg-muted/70 text-muted-foreground hover:text-foreground'
                }`}
              >
                {arch.label}
              </button>
            ))}
          </div>
        )}

        {/* Modal Scroll Content */}
        <div className="flex-1 overflow-y-auto p-5 no-scrollbar">
          {activeTab === 'bundles' ? (
            /* 10 Full Website Bundles Grid */
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredBundles.map((bundle) => (
                <div
                  key={bundle.id}
                  className="rounded-xl border border-border bg-card p-4 hover:border-primary transition-all flex flex-col justify-between shadow-xs hover:shadow-md group"
                >
                  <div className="space-y-2.5">
                    {/* Header */}
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-sm sm:text-base text-foreground group-hover:text-primary transition-colors">
                            {bundle.fancyName}
                          </h3>
                          <span className="text-[10px] uppercase font-mono px-1.5 py-0.5 rounded bg-muted font-semibold text-muted-foreground">
                            5 Pages
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                          {bundle.tagline}
                        </p>
                      </div>

                      {/* Palette Swatches */}
                      <div className="flex items-center gap-1 shrink-0 bg-muted/60 p-1 rounded-md border border-border/50">
                        <span
                          className="h-3.5 w-3.5 rounded-full shadow-xs"
                          style={{ backgroundColor: bundle.palette.primary }}
                          title="Primary Accent"
                        />
                        <span
                          className="h-3.5 w-3.5 rounded-full shadow-xs"
                          style={{ backgroundColor: bundle.palette.secondary }}
                          title="Secondary Accent"
                        />
                        <span
                          className="h-3.5 w-3.5 rounded-full shadow-xs border border-border"
                          style={{ backgroundColor: bundle.palette.background }}
                          title="Canvas Background"
                        />
                      </div>
                    </div>

                    {/* Target Audience Badge */}
                    <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                      <span className="font-medium text-foreground">For:</span>
                      <span>{bundle.targetAudience}</span>
                    </div>

                    {/* Included Pages List */}
                    <div className="p-2.5 rounded-lg bg-muted/30 border border-border/60 space-y-1">
                      <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider block mb-1">
                        Included Interchangeable Pages:
                      </span>
                      <div className="grid grid-cols-2 gap-1 text-[11px]">
                        {bundle.pages.map((p) => (
                          <div key={p.id} className="flex items-center gap-1.5 text-foreground/80 truncate">
                            <Check className="h-3 w-3 text-emerald-500 shrink-0" />
                            <span className="truncate">{p.title} ({p.defaultPath})</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="pt-4 border-t border-border/60 flex items-center justify-between gap-2 mt-3">
                    <span className="text-[10px] text-muted-foreground font-mono">
                      Navbar: {bundle.navbarVariant} · Footer: {bundle.footerVariant}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        onApplyFullBundle(bundle);
                        onClose();
                      }}
                      className="px-3.5 py-1.5 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground font-bold text-xs shadow-xs transition-all flex items-center gap-1.5 cursor-pointer hover:scale-[1.02]"
                    >
                      <span>Apply Full Bundle</span>
                      <ArrowRight className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* 50+ Interchangeable Page Templates */
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
              {filteredTemplates.map((tpl) => (
                <div
                  key={tpl.id}
                  className="rounded-xl border border-border bg-card p-3 hover:border-primary transition-all flex flex-col justify-between shadow-xs hover:shadow-sm group"
                >
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span
                        className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded text-white shadow-xs font-mono"
                        style={{ backgroundColor: tpl.accentColor }}
                      >
                        {tpl.archetypeLabel}
                      </span>
                      <span className="text-[10px] text-muted-foreground font-medium">
                        {tpl.bundleName}
                      </span>
                    </div>

                    <h4 className="font-bold text-xs sm:text-sm text-foreground group-hover:text-primary transition-colors">
                      {tpl.title}
                    </h4>
                    <p className="text-[11px] text-muted-foreground line-clamp-2 leading-relaxed">
                      {tpl.description}
                    </p>
                  </div>

                  <div className="pt-3 border-t border-border/60 flex items-center justify-between gap-2 mt-2">
                    <button
                      type="button"
                      onClick={() => handleApplyToCurrent(tpl)}
                      className="px-2.5 py-1 rounded text-[11px] font-semibold bg-muted hover:bg-muted/80 text-foreground transition-colors cursor-pointer border border-border"
                      title="Replace current page content with this template (preserves site header/footer)"
                    >
                      Use on Current Page
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        onAddNewPageWithTemplate(tpl);
                        onClose();
                      }}
                      className="px-2.5 py-1 rounded text-[11px] font-bold bg-primary text-primary-foreground hover:bg-primary/90 transition-opacity flex items-center gap-1 cursor-pointer"
                      title="Add as a new page in this website"
                    >
                      <Plus className="h-3 w-3" />
                      <span>New Page</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
